/**
 * useIntradayChart.js
 *
 * Fetches 5d of 1-min bars from Yahoo Finance.
 * - During market hours: returns today's session
 * - Market closed: returns most recent completed session (previous day)
 *
 * Indicators computed per bar:
 *   vwap      — cumulative session VWAP
 *   vwapSma   — 20-period SMA of VWAP (smooth intraday trend line)
 *   bbMid/bbUp/bbLo — Bollinger Bands SMA20 ±1σ
 *   rsi       — RSI(14) Wilder smoothed
 *   rsiSig    — 9-period SMA of RSI (signal line)
 */

import { useEffect, useRef, useState } from 'react';
import { fetchChart } from '../lib/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function etHour(ts) {
  // Returns ET hour for a unix-ms timestamp (EDT = UTC-4, EST = UTC-5)
  // Good enough approximation: use UTC-4 (summer/EDT)
  const d = new Date(ts);
  return ((d.getUTCHours() - 4) + 24) % 24 + d.getUTCMinutes() / 60;
}

function isMarketOpen() {
  const now = new Date();
  const dow = now.getUTCDay();
  if (dow === 0 || dow === 6) return false;
  const h = etHour(now.getTime());
  return h >= 9.5 && h < 16;
}

// Group bars by calendar date string (YYYY-MM-DD in ET)
function groupByDate(bars) {
  const map = new Map();
  for (const b of bars) {
    const d = new Date(b.t);
    // shift to ET (UTC-4)
    const etMs = b.t - 4 * 3600 * 1000;
    const etD  = new Date(etMs);
    const key  = `${etD.getUTCFullYear()}-${String(etD.getUTCMonth()+1).padStart(2,'0')}-${String(etD.getUTCDate()).padStart(2,'0')}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(b);
  }
  return map;
}

// ─── Indicators ───────────────────────────────────────────────────────────────

function calcVWAP(bars) {
  let cumPV = 0, cumV = 0;
  return bars.map(b => {
    const tp = (b.h + b.l + b.c) / 3;
    cumPV += tp * (b.v || 0);
    cumV  += (b.v || 0);
    return cumV > 0 ? cumPV / cumV : null;
  });
}

function sma(arr, period) {
  return arr.map((_, i) => {
    if (i < period - 1) return null;
    const slice = arr.slice(i - period + 1, i + 1).filter(v => v != null);
    if (slice.length < period) return null;
    return slice.reduce((a, b) => a + b, 0) / period;
  });
}

function calcBB(closes, period = 20, mult = 1) {
  return closes.map((_, i) => {
    if (i < period - 1) return { mid: null, upper: null, lower: null };
    const slice = closes.slice(i - period + 1, i + 1);
    const mean  = slice.reduce((a, b) => a + b, 0) / period;
    const std   = Math.sqrt(slice.reduce((a, b) => a + (b - mean) ** 2, 0) / period);
    return { mid: mean, upper: mean + mult * std, lower: mean - mult * std };
  });
}

function calcRSI(closes, period = 14) {
  const rsi = new Array(closes.length).fill(null);
  if (closes.length < period + 1) return rsi;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const d = closes[i] - closes[i - 1];
    if (d >= 0) gains += d; else losses -= d;
  }
  let avgG = gains / period, avgL = losses / period;
  rsi[period] = avgL === 0 ? 100 : 100 - 100 / (1 + avgG / avgL);
  for (let i = period + 1; i < closes.length; i++) {
    const d = closes[i] - closes[i - 1];
    avgG = (avgG * (period - 1) + Math.max(d, 0)) / period;
    avgL = (avgL * (period - 1) + Math.max(-d, 0)) / period;
    rsi[i] = avgL === 0 ? 100 : 100 - 100 / (1 + avgG / avgL);
  }
  return rsi;
}

function enrichBars(bars) {
  if (!bars?.length) return [];
  const closes  = bars.map(b => b.c);
  const vwapRaw = calcVWAP(bars);
  const vwapTrend = sma(vwapRaw.map(v => v ?? 0), 20);
  const bb      = calcBB(closes, 20, 1);
  const rsi     = calcRSI(closes, 14);
  const rsiSig  = sma(rsi, 9);

  return bars.map((b, i) => {
    const etMs = b.t - 4 * 3600 * 1000;
    const etD  = new Date(etMs);
    const hh   = String(etD.getUTCHours()).padStart(2, '0');
    const mm   = String(etD.getUTCMinutes()).padStart(2, '0');
    return {
      ...b,
      time:      `${hh}:${mm}`,
      vwap:      vwapRaw[i],
      vwapTrend: vwapTrend[i],
      bbMid:     bb[i].mid,
      bbUp:      bb[i].upper,
      bbLo:      bb[i].lower,
      rsi:       rsi[i],
      rsiSig:    rsiSig[i],
    };
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useIntradayChart(symbol, enabled = true) {
  const [bars,      setBars]      = useState(null);
  const [sessionLabel, setSessionLabel] = useState('');
  const [error,     setError]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!symbol || !enabled) { setLoading(false); return; }
    let cancelled = false;

    const tick = async () => {
      try {
        // 5d gives us a few recent sessions of 1-min bars
        const res = await fetchChart(symbol, '5d', '1m');
        if (cancelled || !res.bars?.length) return;

        const byDate = groupByDate(res.bars);
        const dates  = [...byDate.keys()].sort();

        let sessionBars, label;
        if (isMarketOpen()) {
          // Use the latest (today's) date
          const latest = dates[dates.length - 1];
          sessionBars = byDate.get(latest) || [];
          label = 'TODAY';
        } else {
          // Use previous completed session — last date that has data
          const prev = dates[dates.length - 1];
          sessionBars = byDate.get(prev) || [];
          label = `PREV SESSION · ${prev}`;
        }

        // Filter to regular session hours only (09:30–16:00 ET)
        sessionBars = sessionBars.filter(b => {
          const h = etHour(b.t);
          return h >= 9.5 && h < 16;
        });

        setBars(enrichBars(sessionBars));
        setSessionLabel(label);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    tick();
    timerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') tick();
    }, 30000);

    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [symbol, enabled]);

  return { bars, sessionLabel, error, loading };
}
